import { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  Badge,
  Page,
  Stack,
  useThemeProvider,
} from "@towersystems/roam-common-native";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";
import { Product } from "@towersystems/roam-common/lib/generated-types";
import { useEffect } from "react";
import { PerformStockCountRootParamList } from "../../../../../../types";
import StockCount from "../../../../../../utilities/data/model/StockCount";
import StockCountItem from "../../../../../../utilities/data/model/StockCountItem";
import { useOfflineProductSearch } from "../../../../../../utilities/product";
import {
  useCurrentCountContext,
  useStockCountSearchContext,
} from "../../../../../../utilities/stock-count/hooks";
import { StockCountFooter } from "./components";
import { StockCountContent } from "./components/StockCountContent";
import { StockCountTotals } from "./components/StockCountContent/types";
import { Spacings } from "react-native-ui-lib";
import { useWindowDimensions } from "react-native";
import { SMALL_SCREEN_BREAKPOINT } from "../../../../common";

type StockCountScreenRendererProps = NativeStackScreenProps<
  PerformStockCountRootParamList,
  "StockCountScreen"
> &
  StockCountTotals & {
    stockCount: StockCount;
    stockCountItems: StockCountItem[];
  };

export function StockCountScreenRenderer({
  stockCount,
  stockCountItems,
  navigation,
  ...stockCountContentProps
}: StockCountScreenRendererProps) {
  const online = useOnline();
  const layout = useWindowDimensions();

  const { spacings } = useThemeProvider();

  const { stockAdjuster } = useCurrentCountContext();

  const { fullTextSearch, showProductView } = useStockCountSearchContext();

  const {
    data: offlineItems,
    loading: offlineLoading,
    searchProducts,
  } = useOfflineProductSearch();

  const { setSearchFocused } = stockAdjuster;

  useEffect(() => {
    if (!online) {
      searchProducts(fullTextSearch.query);
    }
  }, [online, fullTextSearch.query]);

  const normalizedData = online
    ? {
        items:
          fullTextSearch.items?.map((item) => item.entity as Product) || [],
        loading: fullTextSearch.loading,
      }
    : { items: offlineItems, loading: offlineLoading };

  const isEmptyQuery = fullTextSearch.query === "" || !fullTextSearch.query;

  const items =
    isEmptyQuery || normalizedData.loading ? [] : normalizedData.items;

  return (
    <>
      <StockCountFooter />
      <Page
        cover
        scrollable={true}
        footerStyles={{
          paddingVertical: spacings.s4,
          paddingHorizontal: 0,
        }}
        title={
          layout.width > SMALL_SCREEN_BREAKPOINT
            ? stockCount.name || "Stock Count"
            : undefined
        }
        badges={
          layout.width > SMALL_SCREEN_BREAKPOINT ? (
            <Stack spacing={Spacings.s3}>
              <Badge
                countSource={
                  stockCount.source === "INTERNAL"
                    ? "SOURCE_ROAM"
                    : "SOURCE_RETAILER"
                }
              />
            </Stack>
          ) : undefined
        }
      >
        <StockCountContent
          {...stockCountContentProps}
          {...{
            setSearchFocused,
            query: fullTextSearch.query,
            loading: false,
            items,
            showProductView,
          }}
          compact={layout.width <= SMALL_SCREEN_BREAKPOINT}
        />
      </Page>
    </>
  );
}
